import React from 'react';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { Stack, Typography, Chip, IconButton, Box, LinearProgress, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTBContext } from '../Contexts/useTBStates';
import { formatDateForDisplay, isEventPast } from '@moment-golf/shared';
import { EventPublishStatusIcon } from './EventPublishStatusIcon';
import { eventListCol } from './eventListColumns';
import {
    getEventFillRate,
    getEventPublishIconKind,
    getEventStatusChipSx,
    getEventStatusLabel,
} from '../utils/eventCardStatus';

type EventListEvent = {
    _id: string;
    eventName: string;
    courseName: string;
    startDate: string;
    endDate: string;
    totalRounds: number;
    totalHoles: number;
    published: boolean;
    canceled: boolean;
    playerCap?: number;
    playType?: string | null;
    privacyRegType?: string | null;
    registrationCount?: number;
};

/** Extracted so each row gets its own props and avoids stale closure / key reuse issues */
const EventListRow = React.memo(({ event, appTheme, onSelect }: {
    event: EventListEvent;
    appTheme: any;
    onSelect: () => void;
}) => {
    const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
    const formatDate = () => {
        if (new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })) {
            return formatDateForDisplay(event.startDate, opts);
        }
        return formatDateForDisplay(event.startDate, opts) + ' - ' + formatDateForDisplay(event.endDate, opts);
    };

    const statusLabel = getEventStatusLabel(event);
    const publishKind = getEventPublishIconKind(event);
    const fillRate = getEventFillRate(event.playerCap, event.registrationCount);
    const registrationCount = event.registrationCount ?? 0;
    const playerCap = event.playerCap;
    const fillRateTooltip =
        playerCap != null && playerCap > 0
            ? `${Math.round(fillRate)}% filled — ${registrationCount} of ${playerCap} registered`
            : `${registrationCount} registered`;

    return (
        <Stack
            id={`tb-overview-event-${event._id}`}
            direction="row"
            spacing={2}
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                justifyContent: 'flex-start',
                alignItems: 'center',
                borderBottom: `1px solid ${appTheme.palette.primary.extraLight}`,
                padding: '8px 15px',
                borderRadius: 1,
                cursor: 'pointer',
            }}
            onClick={onSelect}
        >
            <Typography variant="caption" sx={{ fontWeight: 500, ...eventListCol.eventName }}>
                {statusLabel && (
                    <Chip label={statusLabel} size="small" sx={getEventStatusChipSx(statusLabel, { fontSize: 10, mr: 1 })} />
                )}
                {event.eventName}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, ...eventListCol.course }}>
                {event.courseName}
            </Typography>
            <Typography variant="caption" sx={eventListCol.dates}>{formatDate()}</Typography>
            <Typography variant="caption" sx={eventListCol.rounds}>{event.totalRounds}</Typography>
            <Typography variant="caption" sx={eventListCol.holes}>{event.totalHoles}</Typography>
            <Box sx={{ ...eventListCol.field, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Tooltip title={fillRateTooltip} arrow placement="top">
                    <Box sx={{ width: '100%', maxWidth: 56, display: 'flex' }}>
                        <LinearProgress
                            variant="determinate"
                            value={fillRate}
                            aria-label="Field fill rate"
                            sx={{
                                width: '100%',
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'sepia.extraLight',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: 'primary.light',
                                },
                            }}
                        />
                    </Box>
                </Tooltip>
            </Box>
            <Typography variant="caption" sx={eventListCol.playType}>{event.playType || '—'}</Typography>
            <Typography variant="caption" sx={eventListCol.privacy}>{event.privacyRegType || '—'}</Typography>
            <Box sx={{ ...eventListCol.status, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <EventPublishStatusIcon kind={publishKind} fontSize={20} />
            </Box>
        </Stack>
    );
});

EventListRow.displayName = 'EventListRow';

interface EventListSectionProps {
    year: number;
    togglePastEvents: boolean;
    handleSelectEvent: (event: any) => void;
}

export const EventListSection = ({ year, togglePastEvents, handleSelectEvent }: EventListSectionProps) => {
    const { appTheme, factor, eventData } = useTBContext();
    const currentYear = new Date().getUTCFullYear();
    const [open, setOpen] = useState(year === currentYear);
    const yearEvents = eventData
        .filter((event: any) => ((togglePastEvents || !isEventPast(event.endDate)) && event.year === year))
        .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return (
        <Stack key={year} width="100%" justifyContent="space-evenly">
            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
                sx={{ borderBottom: open ? `1px dotted ${appTheme.palette.primary.main}` : 'none' }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'left' }}>
                    {year} Season ({yearEvents.length})
                </Typography>
                <IconButton
                    sx={{ width: 30, height: 30 }}
                    onClick={() => setOpen(!open)}
                >
                    {open ?
                        <ArrowDropUp sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                        :
                        <ArrowDropDown sx={{ color: appTheme.palette.primary.main, fontSize: 30 * factor }} />
                    }
                </IconButton>
            </Stack>
            {open && (
                <Stack direction="column" spacing={0.5} sx={{ flex: 1, width: '100%' }}>
                    {yearEvents.map((event: any, index: number) => (
                            <EventListRow
                                key={String(event._id ?? `evt-${year}-${index}`)}
                                event={event}
                                appTheme={appTheme}
                                onSelect={() => handleSelectEvent(event)}
                            />
                        ))}
                </Stack>
            )}
        </Stack>
    );
};
